interface SkeletonProps {
  className?: string
  variant?: 'text' | 'circular' | 'rectangular'
  width?: string | number
  height?: string | number
}

/**
 * Skeleton per loading placeholders
 */
export default function Skeleton({
  className = '',
  variant = 'text',
  width,
  height,
}: SkeletonProps) {
  const variantStyles = {
    text: 'rounded',
    circular: 'rounded-full',
    rectangular: 'rounded-lg',
  }

  const style: React.CSSProperties = {
    width: width ?? (variant === 'circular' ? height : '100%'),
    height: height ?? (variant === 'text' ? '1em' : variant === 'circular' ? width : 'auto'),
  }

  return (
    <div
      className={`bg-gray-700/50 animate-pulse ${variantStyles[variant]} ${className}`}
      style={style}
      aria-hidden="true"
    />
  )
}

/**
 * Skeleton predefinito per una LocaleCard
 */
export function LocaleCardSkeleton() {
  return (
    <div className="bg-night-light rounded-xl overflow-hidden shadow-lg">
      {/* Immagine */}
      <Skeleton variant="rectangular" className="aspect-[16/9]" />

      {/* Contenuto */}
      <div className="p-4 space-y-3">
        {/* Badge */}
        <div className="flex gap-2">
          <Skeleton width={80} height={20} className="rounded-full" />
          <Skeleton width={40} height={20} className="rounded-full" />
        </div>

        {/* Titolo */}
        <Skeleton variant="text" height={28} className="w-3/4" />

        {/* Località */}
        <Skeleton variant="text" height={16} className="w-1/2" />

        {/* Specialità */}
        <div className="flex gap-2 pt-2">
          <Skeleton width={60} height={16} className="rounded-full" />
          <Skeleton width={50} height={16} className="rounded-full" />
          <Skeleton width={70} height={16} className="rounded-full" />
        </div>
      </div>
    </div>
  )
}

/**
 * Skeleton predefinito per una lista di locali
 */
export function LocaleListSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {Array.from({ length: count }).map((_, i) => (
        <LocaleCardSkeleton key={i} />
      ))}
    </div>
  )
}

/**
 * Skeleton per il dettaglio locale
 */
export function LocaleDetailSkeleton() {
  return (
    <div className="space-y-8">
      {/* Hero immagine */}
      <Skeleton variant="rectangular" className="w-full h-64 md:h-96 rounded-xl" />

      {/* Contenuto */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Colonna principale */}
        <div className="lg:col-span-2 space-y-6">
          {/* Titolo e badge */}
          <div className="space-y-3">
            <div className="flex gap-2">
              <Skeleton width={100} height={24} className="rounded-full" />
              <Skeleton width={50} height={24} className="rounded-full" />
            </div>
            <Skeleton variant="text" height={40} className="w-2/3" />
            <Skeleton variant="text" height={20} className="w-1/2" />
          </div>

          {/* Descrizione */}
          <div className="space-y-2">
            <Skeleton variant="text" height={16} />
            <Skeleton variant="text" height={16} />
            <Skeleton variant="text" height={16} className="w-3/4" />
          </div>

          {/* Perché approvato */}
          <div className="space-y-2">
            <Skeleton variant="text" height={24} className="w-1/3" />
            <Skeleton variant="text" height={16} />
            <Skeleton variant="text" height={16} className="w-5/6" />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <Skeleton variant="rectangular" height={200} className="rounded-xl" />
          <Skeleton variant="rectangular" height={150} className="rounded-xl" />
        </div>
      </div>
    </div>
  )
}
